import { IconArrowRight16 } from '@warp-ds/icons/react';
import { Card, Clickable } from '@warp-ds/react';
// @ts-ignore need to upgrade React types (v17 -> v18) as useId is only in v18
import { type ComponentType, useId } from 'react';
import { thousandSeparated } from 'shared/util/formatting';

import type { CityPlace, PlaceWithExists } from '@/interfaces/place';

type Props = {
    origin: CityPlace;
    destinations: PlaceWithExists[];
    title: string;
    showPrices?: boolean;
    Icon: ComponentType;
};

const placeName = (place: CityPlace) => `${place.city}, ${place.country}`;

export const Recommendations = ({ origin, destinations, title, showPrices = false, Icon }: Props) => {
    const seoOriginPath = `/reise/flybilletter/${origin.countryPath}/${origin.cityPath}`;
    const frontpageOriginPath = `/reise/?override=true&origin=${origin.airport}&originName=${placeName(origin)}`;
    const allHavePrices = destinations.every((destination) => destination.place.price !== null);
    const titleId = useId();

    const Destination = ({ destination }: { destination: PlaceWithExists }) => {
        const { place, pageExists } = destination;
        const destinationName = placeName(place);

        let originDestinationPath: string;
        if (pageExists) {
            originDestinationPath = `${seoOriginPath}/${place.countryPath}/${place.cityPath}/`;
        } else {
            originDestinationPath = `${frontpageOriginPath}&destination=${place.airport}&destinationName=${destinationName}`;
        }

        const cardId = useId();

        return (
            <Card flat className="p-16">
                <article aria-owns={cardId}>
                    <h3 id={cardId} className="m-0">
                        <Clickable
                            href={originDestinationPath}
                            title={`${destinationName} →`}
                            className="text-16 s-text hover:no-underline">
                            <div className="flex ">
                                <div className="flex-none w-40">
                                    <Icon />
                                </div>
                                <div className="grow">
                                    <div>{destinationName}</div>
                                    {showPrices && allHavePrices ? (
                                        <div className="font-normal">
                                            Fra <span className="font-bold">{thousandSeparated(place.price)} kr</span> per person
                                        </div>
                                    ) : null}
                                </div>
                                <div className="flex-none mt-4">
                                    <IconArrowRight16 />
                                </div>
                            </div>
                        </Clickable>
                    </h3>
                </article>
            </Card>
        );
    };

    return (
        <section className="mb-32" aria-labelledby={titleId}>
            <h2 id={titleId} className="text-22 mb-24">
                {title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-16">
                {destinations.map((destination: PlaceWithExists) => (
                    <Destination key={`${destination.place.countryPath}-${destination.place.cityPath}`} destination={destination} />
                ))}
            </div>
        </section>
    );
};
